import React, { useState, useEffect } from 'react';
import { ref, get, set, update } from 'firebase/database';
import { doc, updateDoc } from 'firebase/firestore';
import { database, db } from '../firebase';
import '../styles/Dashboard.css';

const LicenseAdminPanel = () => {
  const [licenses, setLicenses] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [foundUser, setFoundUser] = useState(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expirationFilter, setExpirationFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'

  useEffect(() => {
    loadLicenses();
  }, []);

  const loadLicenses = async () => {
    try {
      const licensesRef = ref(database, 'licenses');
      const snapshot = await get(licensesRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const now = Date.now();
        const licensesArray = Object.entries(data).map(([userId, licenseData]) => {
          const expiration = licenseData.endDate || licenseData.expirationDate || licenseData.premiumEndDate;
          const isExpired = expiration && expiration < now;
          const isTrial = licenseData.licenseType === 'trial' || licenseData.isTrial === true;

          return {
            id: userId,
            email: licenseData.email || 'Email no disponible',
            activa: licenseData.isActive || false,
            fechaExpiracionRaw: expiration,
            fechaExpiracion: expiration ? new Date(expiration).toISOString().split('T')[0] : 'N/A',
            tipo: licenseData.licenseType || 'premium',
            fechaActivacion: licenseData.activationDate || null,
            isExpired,
            isTrial
          };
        });
        setLicenses(licensesArray);
      }
    } catch (error) {
      console.error('Error cargando licencias:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLicenses = licenses.filter(l => {
    const matchesSearch = l.email.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'all' || l.tipo === typeFilter;

    let matchesStatus = true;
    if (statusFilter === 'active') matchesStatus = l.activa && !l.isExpired;
    else if (statusFilter === 'expired') matchesStatus = l.isExpired;
    else if (statusFilter === 'trial') matchesStatus = l.isTrial;
    else if (statusFilter === 'inactive') matchesStatus = !l.activa;
    // El nuevo ciclo de filtrado por clic usará 'active' e 'inactive' mayormente

    let matchesExpiration = true;
    const now = Date.now();
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    const oneMonth = 30 * 24 * 60 * 60 * 1000;

    if (expirationFilter === 'soon-7') {
      matchesExpiration = l.fechaExpiracionRaw && (l.fechaExpiracionRaw - now) <= oneWeek && !l.isExpired;
    } else if (expirationFilter === 'soon-30') {
      matchesExpiration = l.fechaExpiracionRaw && (l.fechaExpiracionRaw - now) <= oneMonth && !l.isExpired;
    } else if (expirationFilter === 'expired') {
      matchesExpiration = l.isExpired;
    } else if (expirationFilter === 'lifetime') {
      matchesExpiration = l.tipo === 'lifetime';
    }

    return matchesSearch && matchesType && matchesStatus && matchesExpiration;
  }).sort((a, b) => {
    const timeA = a.fechaExpiracionRaw || 0;
    const timeB = b.fechaExpiracionRaw || 0;

    if (sortOrder === 'asc') return timeA - timeB;
    return timeB - timeA;
  });

  const toggleLicenseStatus = async (userId, currentStatus) => {
    try {
      const updates = {};
      const newStatus = !currentStatus;

      updates[`licenses/${userId}/isActive`] = newStatus;
      updates[`licenses/${userId}/lastModified`] = Date.now();
      updates[`licencesPremium/${userId}/isActive`] = newStatus;
      updates[`licencesPremium/${userId}/isPremium`] = newStatus;

      await update(ref(database), updates);

      // Actualizar estado local
      setLicenses(licenses =>
        licenses.map(l =>
          l.id === userId || l.email === email ? { ...l, activa: newStatus } : l
        )
      );

      alert(`Licencia ${newStatus ? 'activada' : 'desactivada'} exitosamente`);
    } catch (error) {
      console.error('Error cambiando estado de licencia:', error);
      alert('Error al cambiar el estado de la licencia');
    }
  };

  const extendLicense = async (userId, days) => {
    try {
      const licenseRef = ref(database, `licenses/${userId}`);
      const snapshot = await get(licenseRef);

      if (snapshot.exists()) {
        const licenseData = snapshot.val();
        const currentExpiration = licenseData.endDate || licenseData.expirationDate || licenseData.premiumEndDate || Date.now();
        const newExpiration = currentExpiration + (days * 24 * 60 * 60 * 1000);

        const updates = {};
        updates[`licenses/${userId}/endDate`] = newExpiration;
        updates[`licencesPremium/${userId}/endDate`] = newExpiration;
        updates[`licencesPremium/${userId}/premiumEndDate`] = newExpiration;
        updates[`licencesPremium/${userId}/tokenPeriodEnd`] = newExpiration;

        await update(ref(database), updates);

        loadLicenses(); // Recargar datos
        alert(`Licencia extendida ${days} días exitosamente`);
      }
    } catch (error) {
      console.error('Error extendiendo licencia:', error);
      alert('Error al extender la licencia');
    }
  };

  const handleManualSearch = async () => {
    if (!searchEmail.trim()) return;
    setSearching(true);
    setFoundUser(null);
    try {
      const normalizedEmail = searchEmail.trim().toLowerCase();
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);

      if (snapshot.exists()) {
        const users = snapshot.val();
        let matchedKey = null;
        let matchedUser = null;
        
        for (const [key, u] of Object.entries(users)) {
          if (u.email?.toLowerCase() === normalizedEmail) {
            matchedKey = key;
            matchedUser = u;
            break;
          }
        }

        if (matchedUser) {
          // Verificar si ya tiene licencia
          const existingLic = licenses.find(l => l.email === matchedUser.email);
          setFoundUser({ ...matchedUser, hasLicense: !!existingLic, rtdbKey: matchedKey });
        } else {
          alert('No se encontró ningún usuario con ese correo en la base de datos de registros.');
        }
      }
    } catch (error) {
      console.error('Error buscando usuario:', error);
    } finally {
      setSearching(false);
    }
  };

  const grantManualLicense = async (user, days) => {
    if (!confirm(`¿Otorgar suscripción premium de ${days} días a ${user.email}?`)) return;

    try {
      const startTime = Date.now();
      const endTime = startTime + (days * 24 * 60 * 60 * 1000);
      const uid = user.userId || user.uid || 'N/A';

      const licenseData = {
        userId: uid,
        email: user.email,
        isActive: true,
        isPremium: true,
        startDate: startTime,
        endDate: endTime,
        lastPaymentId: 'MANUAL_ADMIN_' + startTime,
        fullName: user.fullName || user.displayName || '',
        phone: user.phone || ''
      };

      const updates = {};
      
      // 1. RTDB licenses
      updates[`licenses/${uid}`] = licenseData;
      
      // 2. RTDB licencesPremium
      updates[`licencesPremium/${uid}`] = {
        ...licenseData,
        tokensAvailable: 2000000,
        tokensUsed: 0,
        tokenPeriodStart: startTime,
        tokenPeriodEnd: endTime
      };
      
      // 3. RTDB users
      if (user.rtdbKey) {
        updates[`users/${uid}/isPremium`] = true;
        updates[`users/${uid}/endDate`] = endTime;
        updates[`users/${uid}/startDate`] = startTime;
      }

      await update(ref(database), updates);
      
      // 4. Firestore users
      if (uid !== 'N/A') {
        try {
          const userDocRef = doc(db, 'users', uid);
          await updateDoc(userDocRef, {
            isPremium: true,
            endDate: endTime,
            startDate: startTime,
            licenseType: 'premium'
          });
        } catch (fsError) {
          console.warn('No se pudo actualizar Firestore users (puede que el documento no exista):', fsError);
        }
      }

      alert(`Licencia de ${days} días otorgada exitosamente y base de datos actualizada`);
      setFoundUser(null);
      setSearchEmail('');
      loadLicenses();
    } catch (error) {
      console.error('Error otorgando licencia:', error);
      alert('Error al otorgar la licencia');
    }
  };

  const exportLicensesToCSV = () => {
    if (!filteredLicenses.length) {
      alert('No hay licencias para exportar');
      return;
    }
    const headers = ['Email', 'Tipo', 'Estado', 'Fecha Expiracion', 'ID Usuario'];
    const rows = filteredLicenses.map(l => [
      l.email,
      l.isTrial ? 'Trial' : (l.tipo || 'Premium'),
      l.activa ? (l.isExpired ? 'Vencida' : 'Activa') : 'Inactiva',
      l.fechaExpiracion,
      l.id
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' 
      + [headers.join(','), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `reporte_licencias_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <div className="loading">Cargando licencias...</div>;
  }

  return (
    <div className="license-admin-panel">
      <div className="panel-section">
        <h3>Activación Manual</h3>
        <p className="section-desc">Busca usuarios registrados que aún no tienen licencia o requieren activación forzada.</p>
        <div className="search-manual-container" style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <input
            type="email"
            placeholder="Email exacto del usuario..."
            value={searchEmail}
            onChange={e => setSearchEmail(e.target.value)}
            className="manual-search-input"
            style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}
          />
          <button
            onClick={handleManualSearch}
            disabled={searching || !searchEmail}
            className="search-btn-primary"
            style={{ padding: '0 25px', borderRadius: '8px', background: 'var(--accent)', color: 'white', border: 'none', cursor: 'pointer' }}
          >
            {searching ? 'Buscando...' : '🔍 Buscar Usuario'}
          </button>
        </div>

        {foundUser && (
          <div className="found-user-card" style={{ padding: '20px', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--accent)', marginBottom: '30px' }}>
            <h4>Usuario Encontrado: {foundUser.fullName || foundUser.displayName || 'Sin nombre'}</h4>
            <p>📧 {foundUser.email}</p>
            <p>📱 {foundUser.phone || 'Sin teléfono'}</p>
            <p>🆔 {foundUser.userId || foundUser.uid}</p>
            {foundUser.hasLicense ? (
              <div style={{ color: 'var(--success-color)', fontWeight: 'bold', marginTop: '10px' }}>
                ✅ Este usuario ya posee una licencia registrada.
              </div>
            ) : (
              <div style={{ marginTop: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => grantManualLicense(foundUser, 30)}
                  className="grant-btn"
                  style={{ padding: '10px 20px', background: 'var(--success-color)', color: 'white', border: 'none', borderRadius: '50px', cursor: 'pointer' }}
                >
                  💎 30 Días
                </button>
                <button
                  onClick={() => grantManualLicense(foundUser, 60)}
                  className="grant-btn"
                  style={{ padding: '10px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: '50px', cursor: 'pointer' }}
                >
                  💎 60 Días
                </button>
                <button
                  onClick={() => grantManualLicense(foundUser, 180)}
                  className="grant-btn"
                  style={{ padding: '10px 20px', background: '#9b59b6', color: 'white', border: 'none', borderRadius: '50px', cursor: 'pointer' }}
                >
                  💎 180 Días
                </button>
                <button
                  onClick={() => grantManualLicense(foundUser, 360)}
                  className="grant-btn"
                  style={{ padding: '10px 20px', background: '#f39c12', color: 'white', border: 'none', borderRadius: '50px', cursor: 'pointer' }}
                >
                  💎 360 Días
                </button>
              </div>
            )}
          </div>
        )}

        <hr style={{ opacity: 0.1, margin: '30px 0' }} />

        <div className="list-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
          <h3>Listado de Licencias ({filteredLicenses.length})</h3>
          <div className="filter-controls" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={exportLicensesToCSV}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: '1px solid #34d399',
                background: 'rgba(52, 211, 153, 0.15)',
                color: '#34d399',
                fontWeight: 'bold',
                fontSize: '0.82rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              title="Exportar esta vista filtrada a CSV/Excel"
            >
              📥 Exportar CSV
            </button>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="filter-select">
              <option value="all">Todos los Estados</option>
              <option value="active">Activas</option>
              <option value="trial">Prueba / Trial</option>
              <option value="expired">Vencidas</option>
              <option value="inactive">Inactivas</option>
            </select>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="filter-select">
              <option value="all">Tipos: Todos</option>
              <option value="premium">👑 Premium</option>
              <option value="trial">🧪 Trial</option>
            </select>
            <select value={expirationFilter} onChange={e => setExpirationFilter(e.target.value)} className="filter-select">
              <option value="all">Expiración: Todas</option>
              <option value="soon-7">Próx. 7 días</option>
              <option value="soon-30">Próx. 30 días</option>
              <option value="expired">Ya vencidas</option>
            </select>
          </div>
        </div>

        <div className="search-section" style={{ marginBottom: '15px' }}>
          <input
            type="text"
            placeholder="Filtrar por correo electrónico..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="dashboard-search compact"
            autoComplete="off"
          />
        </div>

        <div className="licenses-table-container compact">
          <table className="dashboard-table compact">
            <thead>
              <tr>
                <th>Email</th>
                <th>Tipo</th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setStatusFilter(prev => prev === 'all' ? 'active' : (prev === 'active' ? 'inactive' : 'all'))}
                >
                  Estado {statusFilter === 'all' ? '🔍' : (statusFilter === 'active' ? '✅' : '❌')}
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                >
                  Expiración {sortOrder === 'asc' ? '↑' : '↓'}
                </th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredLicenses.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">No se encontraron licencias</td>
                </tr>
              ) : (
                filteredLicenses.map(l => (
                  <tr key={l.id} className={l.isExpired ? 'row-expired' : ''}>
                    <td className="cell-email">{l.email}</td>
                    <td>
                      <span className={`license-badge ${l.tipo} ${l.isTrial ? 'trial' : ''}`}>
                        {l.isTrial ? '🧪 Trial' : '👑 Prem'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-pill ${l.activa ? (l.isExpired ? 'expired' : 'active') : 'inactive'}`}>
                        {l.activa ? (l.isExpired ? 'Vencida' : 'Activa') : 'Bloq'}
                      </span>
                    </td>
                    <td className="cell-date">
                      {l.fechaExpiracion}
                    </td>
                    <td className="cell-actions">
                      <div className="action-buttons-compact">
                        <button
                          onClick={() => toggleLicenseStatus(l.id, l.activa, l.email)}
                          className={`mini-btn ${l.activa ? 'off' : 'on'}`}
                          title={l.activa ? 'Desactivar' : 'Activar'}
                        >
                          {l.activa ? '🔒' : '✅'}
                        </button>
                        <button
                          onClick={() => extendLicense(l.id, 30, l.email)}
                          className="mini-btn add"
                          title="+30 días"
                        >
                          +30d
                        </button>
                        <button
                          onClick={() => extendLicense(l.id, 90, l.email)}
                          className="mini-btn add"
                          title="+90 días"
                        >
                          +90d
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="license-stats">
          <div className="stat-item">
            <div className="stat-number">{licenses.length}</div>
            <div className="stat-label">Total</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{licenses.filter(l => l.activa && !l.isExpired).length}</div>
            <div className="stat-label">Activas</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">{licenses.filter(l => l.tipo === 'premium' || l.tipo === 'gold' || l.tipo === 'lifetime').length}</div>
            <div className="stat-label">Premium</div>
          </div>
        </div>
      </div>

      <style>{`
        .list-header { flex-wrap: wrap; gap: 15px; }
        .filter-controls { flex-wrap: wrap; }
        .filter-select { padding: 8px 10px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.2); color: white; font-size: 0.8em; cursor: pointer; min-width: 120px; }
        .filter-select:focus { border-color: var(--accent); outline: none; }
        
        .dashboard-search.compact { padding: 10px 15px; font-size: 0.9em; margin-bottom: 0; }
        
        .licenses-table-container.compact { background: rgba(0,0,0,0.15); border-radius: 10px; overflow: hidden; border: 1px solid rgba(255,255,255,0.05); }
        .dashboard-table.compact { width: 100%; border-collapse: collapse; font-size: 0.82em; }
        .dashboard-table.compact th { background: rgba(255,255,255,0.03); color: #888; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; padding: 10px 8px; text-align: left; border-bottom: 1px solid rgba(255,255,255,0.05); }
        .dashboard-table.compact td { padding: 8px; border-bottom: 1px solid rgba(255,255,255,0.03); color: #ddd; vertical-align: middle; }
        .dashboard-table.compact tr:hover { background: rgba(255,255,255,0.02); }
        .row-expired { background: rgba(231, 76, 60, 0.05); }
        
        .cell-email { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: monospace; }
        .cell-date { color: #888; white-space: nowrap; }
        
        .license-badge { padding: 2px 6px; border-radius: 4px; font-size: 0.85em; font-weight: 700; text-transform: uppercase; }
        .license-badge.premium { background: rgba(241, 196, 15, 0.2); color: #f1c40f; }
        .license-badge.trial { background: rgba(155, 89, 182, 0.2); color: #9b59b6; }
        
        .status-pill { padding: 2px 6px; border-radius: 20px; font-size: 0.85em; font-weight: 700; }
        .status-pill.active { background: rgba(46, 204, 113, 0.2); color: #2ecc71; }
        .status-pill.expired { background: rgba(231, 76, 60, 0.2); color: #e74c3c; }
        .status-pill.inactive { background: rgba(149, 165, 166, 0.2); color: #95a5a6; }
        
        .action-buttons-compact { display: flex; gap: 4px; }
        .mini-btn { padding: 4px 6px; border-radius: 4px; border: none; font-size: 0.8em; font-weight: 700; cursor: pointer; transition: 0.2s; background: rgba(255,255,255,0.05); color: #fff; }
        .mini-btn:hover { transform: scale(1.1); filter: brightness(1.2); }
        .mini-btn.on { background: rgba(46, 204, 113, 0.3); }
        .mini-btn.off { background: rgba(231, 76, 60, 0.3); }
        .mini-btn.add { background: rgba(52, 152, 219, 0.3); color: #3498db; }
        
        .license-stats { display: flex; gap: 10px; margin-top: 20px; flex-wrap: wrap; }
        .stat-item { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.05); padding: 10px; border-radius: 10px; flex: 1; min-width: 100px; text-align: center; }
        .stat-number { font-size: 1.3em; font-weight: 800; color: var(--accent); margin-bottom: 2px; }
        .stat-label { font-size: 0.65em; color: #888; text-transform: uppercase; letter-spacing: 1px; }
      `}</style>
    </div>
  );
};

export default LicenseAdminPanel;
